import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SegmentedTabs from './SegmentedTabs';
import {
  ListingManageStatusFields,
  ListingModalTitleBar,
  ListingSyncStatusRow,
  ListingSyncUnavailableNotice,
  LISTING_MODAL_HELPER_SX,
} from './listingModalUi';
import ListingIdPicker from './ListingIdPicker';
import { patchListingLayoutLink } from './analyticsListingApi';
import { ANALYTICS_RED } from './analyticsColors';
import {
  parseDateInput,
  requiresOffMarketDate,
  formatDateForInput,
} from './listingMarketDates';
import {
  getListingInputModalDialogProps,
  LISTING_INPUT_MODAL_CONTENT_SX,
} from './listingInputModalStyles';

const STATUS_SOURCE_OPTIONS = [
  { value: 'automatic', label: 'Auto sync' },
  { value: 'manual', label: 'Manual' },
];

/**
 * Single modal for linked listings: listing ID, optional sync, and property status.
 */
const ListingManageModal = ({
  open,
  onClose,
  partner,
  linkId,
  listingRef = '',
  planName = null,
  initialMlsStatus = 'unknown',
  initialCustomPropertyStatus = '',
  initialOnMarketDate = null,
  initialOnMarketDateSource = null,
  initialOffMarketDate = null,
  initialMlsSyncEnabled = false,
  onSuccess,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));
  const dialogProps = getListingInputModalDialogProps(fullScreenDialog);

  const [listingId, setListingId] = useState('');
  const [matchedListing, setMatchedListing] = useState(null);
  const [mlsSyncEnabled, setMlsSyncEnabled] = useState(false);
  const [customPropertyStatus, setCustomPropertyStatus] = useState('');
  const [onMarketDate, setOnMarketDate] = useState('');
  const [offMarketDate, setOffMarketDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setListingId(listingRef || '');
    setMatchedListing(null);
    setMlsSyncEnabled(Boolean(initialMlsSyncEnabled));
    setCustomPropertyStatus(initialCustomPropertyStatus || '');
    setOnMarketDate(formatDateForInput(initialOnMarketDate));
    setOffMarketDate(formatDateForInput(initialOffMarketDate));
    setError(null);
  }, [
    open,
    linkId,
    listingRef,
    initialCustomPropertyStatus,
    initialOnMarketDate,
    initialOffMarketDate,
    initialMlsSyncEnabled,
    initialMlsStatus,
  ]);

  const showOffMarketDate = requiresOffMarketDate(customPropertyStatus);
  const manualLocked = mlsSyncEnabled;
  // Provenance describes the saved date. Once the field has been edited in this
  // session it no longer applies to what is on screen.
  const onMarketDateSource =
    onMarketDate === formatDateForInput(initialOnMarketDate)
      ? initialOnMarketDateSource
      : null;
  const sourceConnected = initialMlsStatus !== 'unknown';
  const autoSyncComingSoon = !sourceConnected && !mlsSyncEnabled;
  const statusSource = mlsSyncEnabled ? 'automatic' : 'manual';
  const statusSourceHelper = useMemo(() => {
    if (autoSyncComingSoon) return null;
    if (mlsSyncEnabled && !sourceConnected) {
      return 'Auto sync is on but we have not received an update yet. Switch to Manual to enter status yourself.';
    }
    if (mlsSyncEnabled) {
      return 'Status and dates update from your listing site. Switch to Manual to enter them yourself.';
    }
    if (sourceConnected) {
      return 'Choose Auto sync to keep status and dates in sync with your listing site.';
    }
    return null;
  }, [mlsSyncEnabled, sourceConnected, autoSyncComingSoon]);

  const handleSave = async () => {
    if (!linkId) {
      setError('This listing is not linked yet.');
      return;
    }

    const ref = listingId.trim();
    if (!ref) {
      setError('Enter a listing ID.');
      return;
    }

    const patch = {
      customer_ref: ref,
      mlsSyncEnabled,
    };

    // Only ask the BE to resolve status from the feed when the ref came from a
    // search match and the admin left this listing on Auto sync. Switching back
    // to Manual is an explicit override that must not be undone by the feed.
    if (matchedListing && matchedListing.listingRef === ref && mlsSyncEnabled) {
      patch.autoSyncListing = true;
    }

    const wantsManualSave =
      !manualLocked &&
      (Boolean(customPropertyStatus) ||
        Boolean(onMarketDate.trim()) ||
        Boolean(offMarketDate.trim()) ||
        Boolean(initialCustomPropertyStatus) ||
        Boolean(initialOnMarketDate));

    if (wantsManualSave) {
      if (!onMarketDate.trim()) {
        setError('Enter the on-market date.');
        return;
      }
      const onIso = parseDateInput(onMarketDate);
      if (onIso === undefined) {
        setError('On-market date is not valid.');
        return;
      }
      if (showOffMarketDate && !offMarketDate.trim()) {
        setError('Enter the off-market date for under contract or sold.');
        return;
      }
      const offIso = showOffMarketDate ? parseDateInput(offMarketDate) : null;
      if (showOffMarketDate && offIso === undefined) {
        setError('Off-market date is not valid.');
        return;
      }

      patch.customPropertyStatus = customPropertyStatus || null;
      patch.onMarketDate = onIso;
      patch.offMarketDate = showOffMarketDate ? offIso : null;
    }

    setSaving(true);
    setError(null);
    try {
      const result = await patchListingLayoutLink(linkId, partner, patch);
      onSuccess?.(result);
      onClose();
    } catch (e) {
      setError(e?.message || 'Could not save listing.');
    } finally {
      setSaving(false);
    }
  };

  const titleLabel = planName?.trim() || (listingRef ? listingRef : 'Linked listing');
  const refNorm = String(listingRef || '').trim().toLowerCase();
  const showTitleSubtitle =
    Boolean(titleLabel) &&
    (!refNorm || titleLabel.trim().toLowerCase() !== refNorm);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} {...dialogProps}>
      <ListingModalTitleBar
        title="Manage listing"
        subtitle={showTitleSubtitle ? titleLabel : null}
        onClose={onClose}
        onSave={handleSave}
        saving={saving}
        saveAriaLabel="Save listing"
      />

      <DialogContent sx={LISTING_INPUT_MODAL_CONTENT_SX}>
        <Stack spacing={{ xs: 2, sm: 2.25 }}>
          <ListingIdPicker
            partner={partner}
            value={listingId}
            onChange={setListingId}
            onSelect={(listing) => {
              setMatchedListing(listing);
              // Picking a real listing is the opt-in to tracking, and it is what
              // makes the Auto sync tab reachable before the first feed update.
              if (listing) setMlsSyncEnabled(true);
            }}
            selectedListing={matchedListing}
            disabled={saving}
          />

          <Stack spacing={1.25}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(0,0,0,0.65)' }}>
              Status updates
            </Typography>
            <SegmentedTabs
              value={statusSource}
              onChange={(next) => setMlsSyncEnabled(next === 'automatic')}
              options={STATUS_SOURCE_OPTIONS.map((opt) => ({
                ...opt,
                label:
                  opt.value === 'automatic' && autoSyncComingSoon
                    ? 'Auto sync (soon)'
                    : opt.label,
                disabled:
                  saving ||
                  (opt.value === 'automatic' &&
                    !sourceConnected &&
                    statusSource !== 'automatic'),
              }))}
            />
            {autoSyncComingSoon ? <ListingSyncUnavailableNotice /> : null}
            {!autoSyncComingSoon ? (
              <ListingSyncStatusRow connected={sourceConnected} synced={mlsSyncEnabled} />
            ) : null}
            {statusSourceHelper ? (
              <Typography sx={{ ...LISTING_MODAL_HELPER_SX, mt: 0 }}>{statusSourceHelper}</Typography>
            ) : null}
          </Stack>

          <ListingManageStatusFields
            compact
            mlsSyncEnabled={manualLocked}
            mlsStatus={initialMlsStatus}
            customPropertyStatus={customPropertyStatus}
            onCustomPropertyStatusChange={setCustomPropertyStatus}
            onMarketDate={onMarketDate}
            onMarketDateSource={onMarketDateSource}
            onOffMarketDate={offMarketDate}
            onOnMarketDateChange={setOnMarketDate}
            onOffMarketDateChange={setOffMarketDate}
            disabled={saving}
            requireOnMarketDate={!manualLocked}
            showDaysPreview
          />

          {error ? (
            <Typography sx={{ fontSize: '0.78rem', color: ANALYTICS_RED, px: 0.25 }}>
              {error}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ListingManageModal;
